import React, { useState } from 'react';
import { Seo } from '../components/Seo';
import { useNavigate, useLocation } from 'react-router-dom';
import { useJuryAuth, formatApiErrorDetail } from '@/context/JuryAuthContext';
import usePageTitle from '@/hooks/usePageTitle';

const JuryLogin = () => {
  usePageTitle('Jury Login');
  const { login } = useJuryAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/jury-chamber';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6 py-20 relative" data-testid="jury-login-page">
      <Seo title="Jury Login · AdAura Awards" description="Private area — not indexed." path="/" noindex />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 50% 40% at 50% 30%, rgba(201,165,108,0.10), transparent 70%)' }} />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-12">
          <div className="text-[var(--gold)] tracking-[0.5em] uppercase text-[10px] mb-3">AdAura Awards 2026</div>
          <h1 className="font-[Libre_Baskerville] text-4xl text-[var(--ink)] tracking-tight">Jury Chamber</h1>
          <p className="text-[var(--ink-muted)] text-sm mt-3 italic">"To recognise the work that defined the year."</p>
          <div className="mt-5 h-px w-16 bg-[var(--gold-soft)] mx-auto" />
        </div>

        <form onSubmit={onSubmit} className="space-y-6 bg-[var(--bg-alt)]/60 backdrop-blur-sm border border-[var(--gold-faint)] p-10" data-testid="jury-login-form">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Juror Email</label>
            <input data-testid="jury-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none py-2 text-[var(--ink)] transition-colors" autoComplete="email" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Password</label>
            <input data-testid="jury-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent border-b border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none py-2 text-[var(--ink)] transition-colors" autoComplete="current-password" />
          </div>
          {error && <div data-testid="jury-login-error" className="text-[var(--terracotta)] text-sm border-l-2 border-[var(--terracotta-soft)] pl-3 py-1">{error}</div>}
          <button type="submit" disabled={busy} data-testid="jury-login-submit" className="w-full bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] tracking-[0.25em] uppercase text-xs py-3 transition-colors disabled:opacity-50">
            {busy ? 'Signing in…' : 'Enter Jury Chamber'}
          </button>
          <div className="text-center text-[10px] tracking-[0.2em] uppercase text-[var(--ink-muted)]/60">
            Marketing Mind · India
          </div>
        </form>
      </div>
    </div>
  );
};

export default JuryLogin;
